/**
 * The elemental shot meter. Everything here paints onto the tracking canvas,
 * so the meter, the grade and the element burst are part of every recording.
 *
 *   meter(ctx, x, y, progress, armed)  - live arc while the arm rises
 *   burst(element, x, y, score)        - queue an element reaction at the wrist
 *   tick(ctx, w, h)                    - advance and paint active reactions
 *
 * Five elements, most aligned to least: Fire, Electricity, Earth, Water, Air.
 * Nothing here judges "good" or "bad"; the tier is how aligned the rep is with
 * the best in the sport, and Air is the wind to set sail toward.
 */

const fx = [];
const rand = (a, b) => a + Math.random() * (b - a);

export function meter(ctx, x, y, progress, armed) {
  const r = 34;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(255,255,255,.22)';
  ctx.beginPath(); ctx.arc(x, y, r, Math.PI * 0.75, Math.PI * 2.25); ctx.stroke();
  if (armed) {
    const end = Math.PI * 0.75 + progress * Math.PI * 1.5;
    const g = ctx.createLinearGradient(x - r, y, x + r, y);
    g.addColorStop(0, '#2ea8ff'); g.addColorStop(0.5, '#ffd23f'); g.addColorStop(1, '#ff6a1f');
    ctx.strokeStyle = g; ctx.shadowColor = '#ff6a1f'; ctx.shadowBlur = 14;
    ctx.beginPath(); ctx.arc(x, y, r, Math.PI * 0.75, end); ctx.stroke();
    // release window marker
    ctx.shadowBlur = 0; ctx.strokeStyle = '#fff'; ctx.lineWidth = 3;
    const a = Math.PI * 0.75 + 0.86 * Math.PI * 1.5;
    ctx.beginPath(); ctx.moveTo(x + Math.cos(a) * (r - 9), y + Math.sin(a) * (r - 9)); ctx.lineTo(x + Math.cos(a) * (r + 9), y + Math.sin(a) * (r + 9)); ctx.stroke();
  }
  ctx.restore();
}

export function burst(element, x, y, score) {
  const now = performance.now();
  const parts = [];
  const n = element.id === 'fire' ? 46 : element.id === 'electricity' ? 10 : element.id === 'earth' ? 18 : element.id === 'water' ? 30 : 26;
  for (let i = 0; i < n; i++) {
    switch (element.id) {
      case 'fire': parts.push({ x: rand(-16, 16), y: rand(-6, 6), vx: rand(-0.4, 0.4), vy: rand(-2.6, -1.2), r: rand(3, 9), life: rand(0.6, 1.1) }); break;
      case 'electricity': parts.push({ a: rand(0, Math.PI * 2), len: rand(40, 90), jag: 5 + Math.floor(rand(0, 4)), life: rand(0.25, 0.6), delay: rand(0, 0.5) }); break;
      case 'earth': parts.push({ x: rand(-40, 40), y: rand(40, 70), vy: rand(-1.6, -0.6), w: rand(6, 16), h: rand(5, 12), rot: rand(0, Math.PI), life: rand(0.8, 1.3) }); break;
      case 'water': parts.push({ a: rand(0, Math.PI * 2), sp: rand(1.2, 2.4), r: rand(3, 6), wob: rand(0, Math.PI * 2), life: rand(0.7, 1.2) }); break;
      default: parts.push({ a: rand(0, Math.PI * 2), rad: rand(10, 30), sp: rand(1.5, 3), len: rand(24, 60), life: rand(0.8, 1.4) });
    }
  }
  fx.push({ element, x, y, score, born: now, ttl: 1500, parts });
}

export function tick(ctx, w, h) {
  const now = performance.now();
  for (let i = fx.length - 1; i >= 0; i--) {
    const f = fx[i];
    const t = (now - f.born) / f.ttl;
    if (t >= 1) { fx.splice(i, 1); continue; }
    paint(ctx, f, t, w, h);
  }
}

function paint(ctx, f, t, w, h) {
  const { element: e, x, y } = f;
  const fade = 1 - Math.max(0, (t - 0.65) / 0.35);
  ctx.save();
  ctx.globalAlpha = fade;

  // element particles
  for (const p of f.parts) {
    const life = Math.min(1, t / p.life);
    if (life >= 1 && e.id !== 'electricity') continue;
    switch (e.id) {
      case 'fire': {
        const px = x + p.x + p.vx * life * 60, py = y + p.y + p.vy * life * 60;
        const g = ctx.createRadialGradient(px, py, 0, px, py, p.r * (1 - life * 0.5));
        g.addColorStop(0, 'rgba(255,240,150,1)'); g.addColorStop(0.4, 'rgba(255,120,30,.9)'); g.addColorStop(1, 'rgba(255,60,0,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(px, py, p.r * (1 - life * 0.5), 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 'electricity': {
        if (t < p.delay || t > p.delay + p.life) break;
        ctx.strokeStyle = Math.random() > 0.5 ? '#ffd23f' : '#fff'; ctx.lineWidth = 2.2; ctx.shadowColor = '#ffd23f'; ctx.shadowBlur = 12;
        ctx.beginPath(); ctx.moveTo(x, y);
        let cx = x, cy = y;
        for (let k = 1; k <= p.jag; k++) {
          const d = (p.len / p.jag) * k;
          cx = x + Math.cos(p.a) * d + rand(-9, 9); cy = y + Math.sin(p.a) * d + rand(-9, 9);
          ctx.lineTo(cx, cy);
        }
        ctx.stroke(); ctx.shadowBlur = 0;
        break;
      }
      case 'earth': {
        const py = y + p.y + p.vy * life * 70, px = x + p.x;
        ctx.fillStyle = life < 0.15 ? '#6d5a3b' : '#8bc34a'; ctx.translate(px, py); ctx.rotate(p.rot + life);
        ctx.fillStyle = '#7a6a4a'; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.fillStyle = 'rgba(139,195,74,.7)'; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * 0.35);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        break;
      }
      case 'water': {
        const d = p.sp * life * 60, wob = Math.sin(life * 8 + p.wob) * 6;
        const px = x + Math.cos(p.a) * d + wob, py = y + Math.sin(p.a) * d * 0.6 + life * 30;
        ctx.fillStyle = `rgba(46,168,255,${0.9 - life * 0.6})`; ctx.beginPath(); ctx.arc(px, py, p.r * (1 - life * 0.3), 0, Math.PI * 2); ctx.fill();
        break;
      }
      default: { // air: swirling wind lines
        const ang = p.a + life * p.sp, rad = p.rad + life * 50;
        ctx.strokeStyle = `rgba(201,214,255,${0.9 - life * 0.7})`; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let k = 0; k <= 8; k++) { const a2 = ang - (k / 8) * 0.9; const rr = rad - k * 2; const px = x + Math.cos(a2) * rr, py = y + Math.sin(a2) * rr * 0.7; k ? ctx.lineTo(px, py) : ctx.moveTo(px, py); }
        ctx.stroke();
      }
    }
  }

  // grade ring + label, pops in then settles
  const pop = t < 0.15 ? t / 0.15 : 1;
  const r = 40 * (0.6 + 0.4 * pop);
  ctx.lineWidth = 7; ctx.strokeStyle = e.color; ctx.shadowColor = e.color; ctx.shadowBlur = 18;
  ctx.beginPath(); ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + (f.score / 100) * Math.PI * 2); ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(0,0,0,.55)'; ctx.beginPath(); ctx.arc(x, y, r - 8, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.font = `900 ${Math.round(r * 0.72)}px Nunito, sans-serif`; ctx.fillText(String(f.score), x, y + 1);
  ctx.font = '900 13px Nunito, sans-serif'; ctx.fillStyle = e.color;
  ctx.fillText(e.label.toUpperCase(), x, y + r + 16);
  ctx.restore();
}

/** Inline SVG icons for the feed chips and shot log. */
export function icon(elementId, size = 14) {
  const s = `width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"`;
  switch (elementId) {
    case 'fire': return `<svg ${s}><path d="M12 2c1 4 5 5 5 10a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3 1-6 1-9z" fill="#ff6a1f"/></svg>`;
    case 'electricity': return `<svg ${s}><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" fill="#ffd23f"/></svg>`;
    case 'earth': return `<svg ${s}><path d="M3 20h18l-4-7-3 3-3-5-4 6z" fill="#8bc34a"/><path d="M8 20l2-5 2 2" stroke="#4e7a2a" stroke-width="1.5"/></svg>`;
    case 'water': return `<svg ${s}><path d="M12 3c3 4 6 7 6 11a6 6 0 0 1-12 0c0-4 3-7 6-11z" fill="#2ea8ff"/></svg>`;
    default: return `<svg ${s}><path d="M3 8h12a3 3 0 1 0-3-3M3 13h16a3 3 0 1 1-3 3M3 18h9" stroke="#8fa8ff" stroke-width="2" stroke-linecap="round"/></svg>`;
  }
}
